import React, { forwardRef, useState } from 'react';
import styled from 'styled-components';

import { useTheme } from '../contexts/ThemeProvider';
import { mainStack, otherTools, skillGroups } from '../data/skills';
import { education, certificates } from '../data/education';

const Skills = forwardRef((props, ref) => {
	const color = useTheme();
	// { group: 'main stack' | 'also use', skill }
	const [hovered, setHovered] = useState(null);

	const iconBoxes = (skills, group) =>
		skills.map((skill) => (
			<div
				className="box"
				key={skill.name}
				onMouseEnter={() => setHovered({ group, skill })}
				onMouseLeave={() => setHovered(null)}
			>
				<img src={skill.icon} alt={skill.name} />
			</div>
		));

	return (
		<SkillContainer color={color.color} showLanguageInfo={hovered !== null} ref={ref} id="skill">
			<div className="header">
				<h1>My Skills</h1>
				<h3>_what i can do?</h3>
			</div>

			<div className="prog-languages">
				<div className="languages">
					<div className="main-stack">
						<div className="language-header">
							<h1 className="lang-header">main stack_</h1>
						</div>
						<div className="boxes">{iconBoxes(mainStack, 'main stack')}</div>
					</div>
					<div className="other-languages">
						<div className="language-header">
							<h1 className="lang-header">also use_</h1>
						</div>
						<div className="boxes">{iconBoxes(otherTools, 'also use')}</div>
					</div>
				</div>

				<div className="overview">
					{hovered && (
						<>
							<div className="language-name">
								<h3>{hovered.skill.name}</h3>
								<div className="tags">
									<div className="years">{hovered.skill.category}</div>
									<div className="level-of-expertise">{hovered.group}</div>
								</div>
							</div>

							<div className="overview-img">
								<img src={hovered.skill.image} alt={hovered.skill.name} />
							</div>
						</>
					)}
				</div>
			</div>

			<h1 className="section-title">toolbox_</h1>

			<div className="toolbox">
				{skillGroups.map((group) => (
					<div className="group" key={group.name}>
						<h3>{group.name}</h3>
						<div className="items">
							{group.items.map((item) => (
								<h4 key={item}>{item}</h4>
							))}
						</div>
					</div>
				))}
			</div>

			<h1 className="section-title">milestones_</h1>

			<div className="school">
				<div className="education">
					<h3>Education</h3>
					<div className="container">
						{education.map((entry) => (
							<div className="entry" key={entry.school}>
								<h5>{entry.school}</h5>
								<p>{entry.program}</p>
								<p className="honor">
									{entry.honor} <span>· {entry.period}</span>
								</p>
							</div>
						))}
					</div>
				</div>

				<div className="certifications">
					<h3>Recognition & Certificates</h3>
					<div className="container">
						{certificates.map((cert) => {
							const content = (
								<>
									{cert.badge && <span className="badge">{cert.badge}</span>}
									<h5>{cert.title}</h5>
									<p>{cert.date}</p>
								</>
							);
							return cert.file ? (
								<a href={cert.file} target="_blank" rel="noreferrer" className="certificate" key={cert.title}>
									{content}
								</a>
							) : (
								<div className="certificate" key={cert.title}>
									{content}
								</div>
							);
						})}
					</div>
				</div>
			</div>
		</SkillContainer>
	);
});

export default Skills;

const SkillContainer = styled.div`
	background-color: ${({ color }) => color.background};
	color: ${({ color }) => color.text};
	width: 100vw;
	padding: 7rem 8rem;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;

	.header {
		align-self: flex-start;
		h1 {
			color: ${({ color }) => color.text};
			text-transform: lowercase;
			font-size: 4rem;
			letter-spacing: 1px;
			font-weight: 800;
		}

		h3 {
			color: ${({ color }) => color.label};
			text-transform: lowercase;
			font-size: 1.5rem;
			letter-spacing: 1.5px;
		}
	}

	.section-title {
		align-self: flex-start;
		font-size: 2.5rem;
		font-weight: 600;
		text-transform: lowercase;
		padding-top: 2rem;
		color: ${({ color }) => color.text};
		letter-spacing: 2px;
	}

	.prog-languages {
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0 0.5rem;
		padding-top: 2rem;
		padding-bottom: 4rem;

		.languages {
			width: 40%;
			height: 100%;
			padding: 0.5rem;
			display: flex;
			flex-direction: column;
			gap: 2rem;
			color: ${({ color }) => color.text};

			.main-stack {
				width: 100%;
				display: flex;
				flex-direction: column;
				gap: 0.5rem;
			}

			.other-languages {
				width: 100%;
				display: flex;
				flex-direction: column;
				gap: 0.5rem;
			}

			.language-header {
				.lang-header {
					font-size: 2.5rem;
					color: ${({ color }) => color.text};
					letter-spacing: 2px;
				}
			}
			.boxes {
				gap: 0.5rem;
				display: flex;
				flex-wrap: wrap;
				gap: 2rem;

				.box {
					display: flex;
					align-items: center;
					justify-content: center;
					width: 6rem;
					height: 6rem;
					border-radius: 3px;
					border: 2px solid ${({ color }) => color.text};
					background-color: ${({ color }) => color.background};
					padding: 1rem;
					transition: all 0.2s ease-in-out;

					&:hover,
					&:active {
						border-radius: 12px;
						transform: translateY(-3px);
						box-shadow: 4px 2px 40px ${({ color }) => color.imageShadow};
						color: ${({ color }) => color.background};
						background-color: ${({ color }) => color.primary};
						img {
							opacity: 1;
						}
					}
					img {
						width: 100%;
						opacity: 0.3;
					}
				}
			}
		}

		.overview {
			width: 60%;
			height: 100%;
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			gap: 1rem;
			z-index: 1000;
			opacity: ${({ showLanguageInfo }) => (showLanguageInfo ? '1' : '0')};
			transition: all 0.3s ease-in-out;
			.language-name {
				width: 100%;
				display: flex;
				flex-direction: column;
				align-items: center;
				justify-content: center;
				gap: 1rem;
				h3 {
					font-size: 3.5rem;
					letter-spacing: 2.5px;
				}
				.tags {
					display: flex;
					align-items: center;
					justify-content: center;
					gap: 1rem;
					div {
						text-transform: lowercase;
						font-size: 1.5rem;
						color: ${({ color }) => color.primary};
						border: 2px solid ${({ color }) => color.primary};
						padding: 0.5rem;
					}
					.level-of-expertise {
						background-color: ${({ color }) => color.primary};
						color: ${({ color }) => color.background};
					}
				}
			}

			.overview-img {
				width: 20rem;
				img {
					width: 100%;
					height: 100%;
					opacity: 0.6;
				}
			}
		}
	}

	.toolbox {
		width: 100%;
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 1.5rem;
		padding: 2rem 0 4rem;

		.group {
			border: 2px solid ${({ color }) => color.primary};
			border-radius: 3px;
			padding: 2rem;
			display: flex;
			flex-direction: column;
			gap: 1.25rem;

			h3 {
				font-size: 2rem;
				text-transform: lowercase;
				letter-spacing: 2px;
			}

			.items {
				display: flex;
				flex-wrap: wrap;
				gap: 0.6rem;
				h4 {
					font-size: 1.05rem;
					border: 2px solid ${({ color }) => color.primary};
					border-radius: 3px;
					padding: 0.2rem 0.8rem;
					color: ${({ color }) => color.text};
					font-weight: 500;
					letter-spacing: 1.5px;
				}
			}
		}
	}

	.school {
		width: 100%;
		display: flex;
		gap: 2rem;
		padding-top: 2rem;

		.education,
		.certifications {
			display: flex;
			flex-direction: column;
			gap: 2rem;
			border: 2px solid ${({ color }) => color.primary};
			border-radius: 3px;
			padding: 4rem 3rem;

			h3 {
				font-size: 3rem;
				text-transform: lowercase;
				text-align: center;
			}

			.container {
				display: flex;
				flex-direction: column;
				gap: 1.5rem;
			}
		}

		.education {
			width: 40%;

			.entry {
				border-left: 1px solid ${({ color }) => color.primary};
				padding-left: 0.75rem;
				display: flex;
				flex-direction: column;
				gap: 0.25rem;
				h5 {
					font-size: 1.5rem;
					color: ${({ color }) => color.text};
				}
				p {
					font-size: 1.1rem;
					color: ${({ color }) => color.label};
				}
				.honor {
					font-weight: bolder;
					color: ${({ color }) => color.text};
					span {
						font-weight: normal;
						color: ${({ color }) => color.label};
					}
				}
			}
		}

		.certifications {
			width: 60%;

			.container {
				display: grid;
				grid-template-columns: 1fr 1fr;
			}

			.certificate {
				display: flex;
				flex-direction: column;
				align-items: flex-start;
				gap: 0.25rem;
				border-left: 1px solid ${({ color }) => color.primary};
				padding-left: 0.75rem;
				transition: 0.1s ease-in;
				.badge {
					font-size: 0.9rem;
					letter-spacing: 1.5px;
					padding: 0.1rem 0.6rem;
					background-color: ${({ color }) => color.primary};
					color: ${({ color }) => color.background};
				}
				h5 {
					font-size: 1.2rem;
					color: ${({ color }) => color.text};
				}
				p {
					font-size: 1.05rem;
					color: ${({ color }) => color.label};
				}
				&:hover,
				&:active {
					transform: translateY(-3px);
				}
			}
		}
	}

	@media (max-width: 1024px){
		padding: 2rem 3rem;
		margin: 3rem 0;

		.header{
			margin-bottom: 4rem;
			h3{
			}
		}
		.prog-languages{
			padding: 0;
			height: 100%;
			margin-bottom: 6rem;
			.languages{
				width: 100%;
				height: auto;

				.main-stack{
					justify-content:center;
					align-items:center;
				}

				.other-languages{
					justify-content:center;
					align-items:center;
				}

				.boxes{
					justify-content:center;
					align-items:center;

					.box{
						width: 9rem;
						height: 9rem;
					}
				}
			}

			.overview{
				width: 0;
				display: none;
				opacity: 0;
			}
		}

		.toolbox{
			grid-template-columns: 1fr 1fr;
		}

		.school{
			flex-direction: column;
			.education{
				width: 100%;
			}

			.certifications{
				width: 100%;
			}
		}
	}

	@media (max-width: 768px){
		padding: 0 2rem;
		margin: 7rem 0;

		.toolbox{
			grid-template-columns: 1fr;
			.group{
				padding: 1.5rem;
			}
		}

		.school{
			.education, .certifications{
				padding: 2.5rem 1.5rem;
			}
			.certifications .container{
				grid-template-columns: 1fr;
			}
		}
	}
`;
